import { useCallback, useEffect, useRef, useState } from 'react';
import { CHAT_DENSITY_EVENT } from '../lib/chatCompactUi.js';
import {
  getDensity,
  setDensity as persistDensity,
  nextDensity,
  normalizeDensity,
} from '../lib/chatDensity.js';
import {
  CHAT_GLOW_EVENT,
  CHAT_GLOW_THEMES,
  cycleChatGlowTheme,
  readChatGlowTheme,
} from '../lib/chatGlowTheme.js';

const VIEW_KEY = 'apolo:chatView';

/**
 * Estado compartilhado da toolbar do ExecutionChat — usado no rail direito
 * (aba Menu) e no próprio feed (barra de busca expandida).
 */
export default function useChatToolbarState() {
  const [density, setDensity] = useState(getDensity);
  const [view, setView] = useState(() => {
    try {
      return localStorage.getItem(VIEW_KEY) === 'timeline' ? 'timeline' : 'conversa';
    } catch {
      return 'conversa';
    }
  });
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [traceOpen, setTraceOpen] = useState(false);
  const [glowTheme, setGlowTheme] = useState(() => readChatGlowTheme());
  const searchInputRef = useRef(null);

  useEffect(() => {
    if (searchOpen) requestAnimationFrame(() => searchInputRef.current?.focus());
  }, [searchOpen]);

  useEffect(() => {
    function onDensityChanged(e) {
      setDensity(normalizeDensity(e?.detail));
    }
    window.addEventListener(CHAT_DENSITY_EVENT, onDensityChanged);
    return () => window.removeEventListener(CHAT_DENSITY_EVENT, onDensityChanged);
  }, []);

  useEffect(() => {
    function onGlowChanged(e) {
      const next = e?.detail;
      if (CHAT_GLOW_THEMES[next]) setGlowTheme(next);
    }
    window.addEventListener(CHAT_GLOW_EVENT, onGlowChanged);
    return () => window.removeEventListener(CHAT_GLOW_EVENT, onGlowChanged);
  }, []);

  const toggleDensity = useCallback(() => {
    setDensity((d) => persistDensity(nextDensity(d)));
  }, []);

  const toggleView = useCallback(() => {
    setView((v) => {
      const next = v === 'timeline' ? 'conversa' : 'timeline';
      try {
        localStorage.setItem(VIEW_KEY, next);
      } catch {
        /* noop */
      }
      return next;
    });
  }, []);

  const toggleGlowTheme = useCallback(() => {
    setGlowTheme(cycleChatGlowTheme());
  }, []);

  const closeSearch = useCallback(() => {
    setSearchOpen(false);
    setQuery('');
  }, []);

  const openSearch = useCallback(() => {
    setSearchOpen(true);
  }, []);

  const toggleTrace = useCallback(() => {
    setTraceOpen((v) => !v);
  }, []);

  return {
    density,
    view,
    searchOpen,
    query,
    setQuery,
    traceOpen,
    glowTheme,
    searchInputRef,
    toggleDensity,
    toggleView,
    toggleGlowTheme,
    closeSearch,
    openSearch,
    toggleTrace,
    setTraceOpen,
  };
}
